import { shell } from '../shell/shellClasses';
import type { BoardViewMode, SortMode } from './types';

const VIEWS: { id: BoardViewMode; label: string }[] = [
  { id: 'active', label: 'Active' },
  { id: 'pinned', label: 'Pinned' },
  { id: 'bookmarks', label: 'Bookmarks' },
  { id: 'completed', label: 'Completed' },
];

const SORTS: { id: SortMode; label: string }[] = [
  { id: 'updated_desc', label: 'Newest updated' },
  { id: 'confidence_desc', label: 'Highest confidence' },
  { id: 'confidence_asc', label: 'Lowest confidence' },
];

export function ActionToolbar({
  view,
  sort,
  onView,
  onSort,
  onRefresh,
  refreshing,
  resultCount,
  search,
  onSearch,
}: {
  view: BoardViewMode;
  sort: SortMode;
  onView: (v: BoardViewMode) => void;
  onSort: (s: SortMode) => void;
  onRefresh: () => void;
  refreshing: boolean;
  resultCount: number;
  search: string;
  onSearch: (q: string) => void;
}) {
  return (
    <div
      className={`flex flex-col gap-3 rounded-[var(--shell-radius-lg)] border p-3 sm:p-4 ${shell.border} ${shell.surface}`}
      role="toolbar"
      aria-label="Decision board toolbar"
    >
      <div className="flex flex-wrap items-center gap-2">
        {VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            className={`${shell.control} ${shell.focusRing} min-h-11 px-3 text-xs ${
              view === v.id ? shell.accentBg : ''
            }`}
            aria-pressed={view === v.id}
            onClick={() => onView(v.id)}
          >
            {v.label}
          </button>
        ))}
        <span className={`ml-auto text-xs tabular-nums ${shell.muted}`}>
          {resultCount} shown
        </span>
        <button
          type="button"
          className={`${shell.control} ${shell.focusRing} min-h-11 px-3 text-xs font-medium`}
          onClick={onRefresh}
          disabled={refreshing}
          aria-busy={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
          <span className={shell.muted}>Search actions</span>
          <input
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Title, evidence, workspace…"
            className={`${shell.control} ${shell.focusRing} w-full`}
            autoComplete="off"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className={shell.muted}>Sort</span>
          <select
            value={sort}
            onChange={(e) => onSort(e.target.value as SortMode)}
            className={`${shell.control} ${shell.focusRing}`}
          >
            {SORTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}
